import { selectCurrentChatId } from './redux/TeamInboxSelectors';
import { selectIsDarkMode } from '@common-reducers/UserSelectors';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import { IconButton, InputBase, Menu, Paper, Tooltip } from '@mui/material';
import EmojiPicker, { EmojiClickData, Theme } from '@/common/components/lazy-emoji-picker/lazy-emoji-picker';
import React, { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface TextWithEmojiInputProps {
    value: string;
    onChange: (value: string) => void;
    onKeyDown: (event: React.KeyboardEvent) => void;
    onPasteFiles?: (files: File[]) => void;
    isLoading: boolean;
    context: 'message' | 'caption';
}

const TextWithEmojiInput: React.FC<TextWithEmojiInputProps> = memo(({
    value,
    onChange,
    onKeyDown,
    onPasteFiles,
    isLoading,
    context
}) => {
    const { t } = useTranslation();
    const chatId = useAppSelector(selectCurrentChatId);
    const isDarkMode = useAppSelector(selectIsDarkMode);

    const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
    const emojiButtonRef = useRef<HTMLButtonElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleEmojiClick = (emojiData: EmojiClickData) => {
        // Insert emoji at cursor position
        let cursorPosition = value.length;
        const activeElement = document.activeElement;

        if (
            (activeElement instanceof HTMLInputElement ||
                activeElement instanceof HTMLTextAreaElement) &&
            typeof activeElement.selectionStart === 'number'
        ) {
            cursorPosition = activeElement.selectionStart;
        }

        const newMessage = value.slice(0, cursorPosition) + emojiData.emoji + value.slice(cursorPosition);
        onChange(newMessage);

        // Close emoji picker
        setEmojiPickerOpen(false);
    };

    const handlePaste = (event: React.ClipboardEvent) => {
        const clipboardData = event.clipboardData;
        const files = Array.from(clipboardData.files);
        
        // Handle file paste if there are files
        if (files.length > 0 && onPasteFiles) {
            event.preventDefault();
            onPasteFiles(files);
            return;
        }
        
        // For text paste, strip HTML styling and only paste plain text
        event.preventDefault();
        const plainText = clipboardData.getData('text/plain');
        
        if (!plainText) return;
        
        // Get cursor position
        const activeElement = document.activeElement;
        if (activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement) {
            const start = activeElement.selectionStart || 0;
            const end = activeElement.selectionEnd || 0;
            
            // Insert plain text at cursor position
            const newValue = value.slice(0, start) + plainText + value.slice(end);
            onChange(newValue);
            
            // Restore cursor position after the pasted text
            setTimeout(() => {
                if (activeElement === document.activeElement) {
                    const newCursorPosition = start + plainText.length;
                    activeElement.setSelectionRange(newCursorPosition, newCursorPosition);
                }
            }, 0);
        } else {
            // Fallback: append to end
            onChange(value + plainText);
        }
    };

    useEffect(() => {
        if (inputRef.current && !isLoading) {
            inputRef.current.focus();
        }
    }, [chatId, isLoading]);

    return (
        <>
            <Paper
                component="form"
                elevation={0}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    // WhatsApp Web composer input: flat filled field, 8px radius,
                    // no border — sits inside the panel-bg composer bar.
                    backgroundColor: 'var(--wa-composer-input-bg)',
                    borderRadius: '8px',
                    padding: context === 'caption' ? '8px 16px' : '4px 16px',
                    border: 'none',
                    ...(context === 'caption' && { mb: 2 }),
                    ...(context === 'message' && { flex: 1 })
                }}
            >
                <InputBase
                    inputRef={inputRef}
                    inputProps={{
                        dir: "auto",
                    }}
                    sx={{
                        ml: 1,
                        flex: 1,
                        fontSize: '15px',
                        fontWeight: 400,
                        color: 'var(--wa-title-text)',
                        '& input::placeholder, & textarea::placeholder': {
                            color: 'var(--wa-secondary-text)',
                            opacity: 1
                        },
                    }}
                    placeholder={context === 'caption' ? t('teamInbox.addCaption') : t('teamInbox.typeMessage')}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={onKeyDown}
                    onPaste={handlePaste}
                    multiline
                    maxRows={4}
                    disabled={isLoading}
                />
                <Tooltip title={t('teamInbox.emoji')}>
                    <IconButton
                        size="small"
                        disabled={isLoading}
                        ref={emojiButtonRef}
                        onClick={() => setEmojiPickerOpen(!emojiPickerOpen)}
                        sx={{
                            color: 'var(--wa-secondary-text)',
                            marginInlineStart: '4px',
                            '&:hover': { backgroundColor: 'transparent', color: 'var(--wa-title-text)' }
                        }}
                    >
                        <InsertEmoticonIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Paper>

            <Menu
                anchorEl={emojiButtonRef.current}
                open={emojiPickerOpen}
                onClose={() => setEmojiPickerOpen(false)}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                sx={{
                    '& .MuiList-root': {
                        padding: 0,
                    },
                }}
            >
                <EmojiPicker 
                    onEmojiClick={handleEmojiClick} 
                    theme={isDarkMode ? Theme.DARK : Theme.LIGHT}
                />
            </Menu>
        </>
    );
});

export default TextWithEmojiInput; 